import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiSearch, FiSliders, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { productCategories } from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

function Shop() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { wishlist, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  const selectedCategory = searchParams.get("category") || "";
  const selectedCollection = searchParams.get("collection") || "";
  const wishlistOnly = searchParams.get("wishlist") === "1";
  const sort = searchParams.get("sort") || "featured";
  const maxPrice = Number(searchParams.get("maxPrice")) || 300;
  const query = (searchParams.get("search") || "").trim().toLowerCase();

  useEffect(() => {
    setSearchInput(searchParams.get("search") || "");
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 180);
    return () => window.clearTimeout(timer);
  }, [searchParams]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "" || value === false || value === null) next.delete(key);
    else next.set(key, String(value));
    navigate(`/shop${next.toString() ? `?${next.toString()}` : ""}`);
  };

  const clearFilters = () => {
    setSearchInput("");
    navigate("/shop");
  };

  const handleSearch = (event) => {
    event.preventDefault();
    updateParam("search", searchInput.trim());
  };

  const filteredProducts = useMemo(() => {
    const products = productData.filter((product) => {
      const searchable = [product.name, product.brand, product.category, ...product.tags]
        .join(" ")
        .toLowerCase();
      return (
        (!selectedCategory || product.category === selectedCategory) &&
        (!selectedCollection || product.collection === selectedCollection) &&
        (!wishlistOnly || wishlist.includes(product.id)) &&
        (!query || searchable.includes(query)) &&
        product.price <= maxPrice
      );
    });

    return [...products].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "rating") return b.rating - a.rating;
      return a.id - b.id;
    });
  }, [maxPrice, query, selectedCategory, selectedCollection, sort, wishlist, wishlistOnly]);

  const pageTitle = wishlistOnly
    ? "Your wishlist"
    : selectedCategory ||
      ({ new: "New Arrivals", limited: "Limited-time Picks", restock: "Restocks" }[
        selectedCollection
      ] || "All stationery");

  const activeFilterCount = [selectedCategory, selectedCollection, query, wishlistOnly, maxPrice < 300]
    .filter(Boolean).length;

  return (
    <main className="shop-page">
      <header className="shop-hero">
        <div className="page-shell">
          <nav aria-label="Breadcrumb"><Link to="/">Home</Link><span>/</span><span>Shop</span></nav>
          <p className="eyebrow">The {siteConfig.brandName} edit</p>
          <h1>{pageTitle}</h1>
          <p>Paper goods and desk tools chosen for thoughtful details, useful design and the pleasure of making a mark by hand.</p>
        </div>
      </header>

      <div className="shop-toolbar page-shell">
        <form className="shop-search" role="search" onSubmit={handleSearch}>
          <label htmlFor="collection-search" className="visually-hidden">Search products</label>
          <FiSearch aria-hidden="true" />
          <input
            id="collection-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search the collection"
          />
          {searchInput && (
            <button type="button" onClick={() => { setSearchInput(""); updateParam("search", ""); }} aria-label="Clear search">
              <FiX />
            </button>
          )}
        </form>

        <button type="button" className="filter-toggle" onClick={() => setFiltersOpen((open) => !open)}>
          <FiSliders /> Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
        </button>

        <div className="shop-sort">
          <label htmlFor="sort-products">Sort by</label>
          <div><select id="sort-products" value={sort} onChange={(event) => updateParam("sort", event.target.value)}>
            <option value="featured">Featured</option>
            <option value="rating">Best rated</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
            <option value="name">Alphabetically</option>
          </select><FiChevronDown aria-hidden="true" /></div>
        </div>
      </div>

      <div className="shop-layout page-shell">
        <aside className={`shop-filters${filtersOpen ? " shop-filters--open" : ""}`} aria-label="Product filters">
          <div className="shop-filters__mobile-head">
            <strong>Filter products</strong>
            <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><FiX /></button>
          </div>

          <details open>
            <summary>Category <FiChevronDown /></summary>
            <div className="filter-list">
              <label><input type="radio" name="category" checked={!selectedCategory} onChange={() => updateParam("category", "")} /> All categories <span>{productData.length}</span></label>
              {productCategories.map((category) => (
                <label key={category}>
                  <input type="radio" name="category" checked={selectedCategory === category} onChange={() => updateParam("category", category)} />
                  {category}
                  <span>{productData.filter((product) => product.category === category).length}</span>
                </label>
              ))}
            </div>
          </details>

          <details open>
            <summary>Availability <FiChevronDown /></summary>
            <div className="filter-list">
              <label><input type="radio" name="collection" checked={!selectedCollection} onChange={() => updateParam("collection", "")} /> All products</label>
              <label><input type="radio" name="collection" checked={selectedCollection === "new"} onChange={() => updateParam("collection", "new")} /> New arrivals</label>
              <label><input type="radio" name="collection" checked={selectedCollection === "limited"} onChange={() => updateParam("collection", "limited")} /> Limited-time picks</label>
              <label><input type="radio" name="collection" checked={selectedCollection === "restock"} onChange={() => updateParam("collection", "restock")} /> Restocks</label>
              <label><input type="checkbox" checked={wishlistOnly} onChange={(event) => updateParam("wishlist", event.target.checked ? "1" : "")} /> In my wishlist</label>
            </div>
          </details>

          <details open>
            <summary>Price <FiChevronDown /></summary>
            <div className="price-filter">
              <div><span>0 kr</span><span>{formatPrice(maxPrice)}</span></div>
              <input type="range" min="40" max="300" step="10" value={maxPrice} onChange={(event) => updateParam("maxPrice", event.target.value)} aria-label="Maximum price" />
            </div>
          </details>

          {activeFilterCount > 0 && <button type="button" className="clear-filters" onClick={clearFilters}>Remove all filters</button>}
        </aside>

        <section className="shop-results" aria-live="polite" aria-busy={isLoading}>
          <div className="shop-results__count">
            <span>{filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"}</span>
            {activeFilterCount > 0 && <button type="button" onClick={clearFilters}>Remove all</button>}
          </div>

          {isLoading ? (
            <div className="shop-grid shop-grid--loading" aria-label="Loading products">
              {Array.from({ length: 8 }, (_, index) => <div className="product-skeleton" key={index}><span /><span /><span /></div>)}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="shop-grid">
              {filteredProducts.map((product) => (
                <article className="product-card" key={product.id}>
                  <div className="product-card__media">
                    <Link to={`/shopDetails/${product.id}`} aria-label={product.name}>
                      <img src={product.image} alt={product.name} loading="lazy" decoding="async" width="900" height="900" />
                    </Link>
                    {product.compareAtPrice && <span className="product-badge">Sale</span>}
                    <button
                      type="button"
                      className={`wishlist-button${isWishlisted(product.id) ? " is-active" : ""}`}
                      onClick={() => toggleWishlist(product.id)}
                      aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                    >
                      <FiHeart />
                    </button>
                  </div>
                  <div className="product-card__content">
                    <p>{product.brand}</p>
                    <h2><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h2>
                    <div className="product-card__price">
                      {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
                      <span>{formatPrice(product.price)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <span aria-hidden="true">⌁</span>
              <h2>No pieces found</h2>
              <p>Try a broader search or remove a filter to return to the full collection.</p>
              <button type="button" className="button" onClick={clearFilters}>View all stationery</button>
            </div>
          )}
        </section>
      </div>
      {filtersOpen && <button className="filter-backdrop" aria-label="Close filters" onClick={() => setFiltersOpen(false)} />}
    </main>
  );
}

export default Shop;
